/** Section 3.2 / 3.5 - the command and reason vocabulary. Closed unions. */

import type {
  ArmyId, ArtifactDefId, BuildingDefId, CreatureDefId, HeroDefId, HeroId,
  InteractionId, ObjectId, PlayerId, SpellDefId, TownId,
} from "./ids.js";
import type { InteractionResponse, ResourceKind } from "./state.js";

// ------------------------------------------------------------ battle commands

export type BattleCommand =
  | { readonly type: "BATTLE_MOVE"; readonly stack: number; readonly hex: number }
  /**
   * `magic` spends one of the stack's `MAGIC_ATTACK` uses to charge the blow.
   * A flag rather than a command of its own because everything an attack does
   * except the damage - the charge, the hazards crossed, the adjacency, the
   * retaliation - is shared, and a second command would be forty duplicated
   * lines for one multiplier.
   */
  | {
      readonly type: "BATTLE_ATTACK";
      readonly stack: number;
      readonly hex: number;
      readonly target: number;
      readonly magic?: boolean | undefined;
    }
  | { readonly type: "BATTLE_SHOOT"; readonly stack: number; readonly target: number }
  | { readonly type: "BATTLE_WAIT"; readonly stack: number }
  /** Sun Serpent meditation: once a battle, a turn spent becoming untouchable. */
  | { readonly type: "BATTLE_MEDITATE"; readonly stack: number }
  /** Steam Colossus: vent, cooking the fan ahead. Spent instead of an attack. */
  | { readonly type: "BATTLE_HEAT_STROKE"; readonly stack: number }
  /** Dune Devourers: eat a corpse instead of acting, banking an extra strike. */
  | { readonly type: "BATTLE_DEVOUR"; readonly stack: number; readonly target: number }
  | { readonly type: "BATTLE_DEFEND"; readonly stack: number }
  | { readonly type: "BATTLE_CAST"; readonly side: 0 | 1; readonly spell: SpellDefId; readonly target: number }
  /**
   * The hero swings instead of casting. Same slot in the round as a spell and
   * mutually exclusive with one - see `battle/heroStrike.ts` - so a might hero
   * with an empty book finally has something to do with it.
   */
  | { readonly type: "BATTLE_HERO_STRIKE"; readonly side: 0 | 1; readonly target: number }
  /**
   * The side's faction racial, spent on the active stack. One charge per battle
   * per side, and what it does is the faction's rather than the command's.
   *
   * (No semicolons in the comments in this union, deliberately.
   * `packages/ui/test/reachability.spec.ts` parses it by scanning to the first
   * statement terminator at brace depth 0 and does not skip comments, so one
   * inside a doc block truncates the union and silently excuses every member
   * below it from the reachability gate. That is how this comment was written
   * the first time, and it hid three commands.)
   */
  | { readonly type: "BATTLE_RACIAL"; readonly side: 0 | 1; readonly stack: number }
  /** A creature's ACTIVATED ability. Spends the turn and starts its cooldown. */
  | { readonly type: "BATTLE_ACTIVATE"; readonly stack: number }
  | { readonly type: "BATTLE_CREATURE_CAST"; readonly stack: number; readonly spell: SpellDefId; readonly target: number }
  | { readonly type: "BATTLE_CATAPULT"; readonly stack: number; readonly wallSegment: number }
  | { readonly type: "BATTLE_HEAL"; readonly stack: number; readonly target: number }
  | { readonly type: "BATTLE_TACTICS_MOVE"; readonly stack: number; readonly hex: number }
  | { readonly type: "BATTLE_TACTICS_DONE"; readonly side: 0 | 1 }
  | { readonly type: "BATTLE_RETREAT"; readonly side: 0 | 1 }
  | { readonly type: "BATTLE_SURRENDER"; readonly side: 0 | 1 };

// --------------------------------------------------------- adventure commands

export type GameCommand =
  | { readonly type: "END_TURN"; readonly player: PlayerId }
  | { readonly type: "MOVE_HERO"; readonly hero: HeroId; readonly path: readonly number[] }
  | { readonly type: "HERO_TO_TOWN"; readonly hero: HeroId; readonly town: TownId }
  | { readonly type: "BUILD"; readonly town: TownId; readonly building: BuildingDefId }
  | { readonly type: "TOGGLE_AUTO_BUILD"; readonly town: TownId }
  /**
   * The expansion spell research. `level` is the guild level whose slot is replaced and
   * `pick` indexes `researchCandidates`, which is derived from the seed, the
   * day and the town - so both offers are ordinary enumerable commands rather
   * than a prompt the host has to answer.
   */
  | {
    readonly type: "RESEARCH_SPELL"; readonly town: TownId;
    readonly level: number; readonly pick: number;
  }
  | { readonly type: "RECRUIT"; readonly town: TownId; readonly creature: CreatureDefId; readonly count: number }
  /**
   * Buy creatures somewhere and have them walk to a town on their own.
   *
   * `fromTown` and `fromObject` are the two things a column can set out from -
   * a town you own, or an external dwelling you own - and exactly one of them
   * is set. Two nullable fields rather than a union arm each, because
   * everything else about the command is identical and a union would duplicate
   * four fields to vary one.
   */
  | {
      readonly type: "SEND_CARAVAN";
      readonly town: TownId;
      readonly fromTown: TownId | null;
      readonly fromObject: ObjectId | null;
      readonly creature: CreatureDefId;
      readonly count: number;
    }
  | { readonly type: "HIRE_HERO"; readonly town: TownId; readonly hero: HeroDefId }
  | {
      readonly type: "UPGRADE_UNITS";
      readonly town: TownId;
      /** The garrison, or a hero standing in the town. */
      readonly army: ArmyId;
      readonly slot: number;
    }
  | { readonly type: "SPLIT_STACK"; readonly army: ArmyId; readonly from: number; readonly to: number; readonly count: number }
  | { readonly type: "MERGE_STACK"; readonly army: ArmyId; readonly from: number; readonly to: number }
  | { readonly type: "SWAP_STACK"; readonly army: ArmyId; readonly a: number; readonly b: number }
  | { readonly type: "TRANSFER_STACK"; readonly fromArmy: ArmyId; readonly fromSlot: number; readonly toArmy: ArmyId; readonly toSlot: number; readonly count: number }
  | { readonly type: "DISMISS_STACK"; readonly army: ArmyId; readonly slot: number }
  | { readonly type: "EQUIP_ARTIFACT"; readonly hero: HeroId; readonly backpackIndex: number; readonly slot: string }
  | { readonly type: "UNEQUIP_ARTIFACT"; readonly hero: HeroId; readonly slot: string }
  /**
   * Hand a bagged artifact to another of the same player's heroes standing
   * beside this one. Only the bag is a source: taking a worn artifact off is
   * already `UNEQUIP_ARTIFACT`, and a host that wants the one-gesture version
   * issues the pair - which is what the classic's drag from a worn slot amounts to.
   */
  | { readonly type: "GIVE_ARTIFACT"; readonly fromHero: HeroId; readonly toHero: HeroId; readonly backpackIndex: number }
  | { readonly type: "ASSEMBLE_ARTIFACT"; readonly hero: HeroId; readonly artifact: ArtifactDefId }
  | { readonly type: "DISASSEMBLE_ARTIFACT"; readonly hero: HeroId; readonly slot: string }
  | { readonly type: "CAST_ADVENTURE_SPELL"; readonly hero: HeroId; readonly spell: SpellDefId; readonly x: number; readonly y: number; readonly z: number }
  | { readonly type: "VISIT_OBJECT"; readonly hero: HeroId; readonly object: ObjectId }
  | { readonly type: "TRADE_RESOURCE"; readonly player: PlayerId; readonly give: ResourceKind; readonly take: ResourceKind; readonly amount: number }
  | { readonly type: "BUY_ARTIFACT"; readonly hero: HeroId; readonly object: ObjectId; readonly artifact: ArtifactDefId }
  | { readonly type: "TOWN_BUY_ARTIFACT"; readonly hero: HeroId; readonly town: TownId; readonly artifact: ArtifactDefId }
  | { readonly type: "DISMISS_HERO"; readonly hero: HeroId }
  | { readonly type: "DIG"; readonly hero: HeroId }
  | { readonly type: "RESPOND_TO_INTERACTION"; readonly id: InteractionId; readonly response: InteractionResponse }
  | { readonly type: "BATTLE"; readonly cmd: BattleCommand };

export type CommandType = GameCommand["type"];

// ------------------------------------------------------------------- reasons

export type Reason =
  | { readonly code: "NOT_YOUR_TURN" }
  | { readonly code: "GAME_OVER" }
  | { readonly code: "PENDING_INTERACTION"; readonly id: InteractionId }
  | { readonly code: "NO_SUCH_ENTITY"; readonly id: string }
  | { readonly code: "NOT_OWNED"; readonly id: string }
  | { readonly code: "INSUFFICIENT_MOVEMENT"; readonly have: number; readonly need: number }
  | { readonly code: "PATH_BLOCKED"; readonly atIndex: number }
  | { readonly code: "NOT_ADJACENT" }
  | { readonly code: "INSUFFICIENT_RESOURCES"; readonly resource: ResourceKind; readonly have: number; readonly need: number }
  | { readonly code: "ALREADY_BUILT"; readonly building: BuildingDefId }
  | { readonly code: "BUILD_LIMIT_REACHED" }
  | { readonly code: "MISSING_PREREQUISITE"; readonly building: BuildingDefId }
  | { readonly code: "NONE_AVAILABLE"; readonly creature: CreatureDefId; readonly have: number; readonly need: number }
  | { readonly code: "ARMY_FULL" }
  | { readonly code: "SLOT_EMPTY"; readonly slot: number }
  | { readonly code: "SLOT_OCCUPIED"; readonly slot: number }
  | { readonly code: "LAST_STACK" }
  | { readonly code: "HERO_LIMIT_REACHED"; readonly limit: number }
  | { readonly code: "OUT_OF_MANA"; readonly have: number; readonly need: number }
  | { readonly code: "SPELL_NOT_KNOWN"; readonly spell: SpellDefId }
  | { readonly code: "NO_SPELLBOOK" }
  | { readonly code: "ALREADY_CAST_THIS_ROUND" }
  | { readonly code: "TARGET_INVALID" }
  | { readonly code: "OUT_OF_RANGE"; readonly distance: number; readonly max: number }
  | { readonly code: "NO_SHOTS_LEFT" }
  | { readonly code: "STACK_NOT_ACTIVE"; readonly stack: number }
  | { readonly code: "STACK_DEAD"; readonly stack: number }
  | { readonly code: "HEX_OCCUPIED"; readonly hex: number }
  | { readonly code: "NO_BATTLE_IN_PROGRESS" }
  | { readonly code: "BATTLE_IN_PROGRESS" }
  | { readonly code: "NOT_IN_TACTICS_PHASE" }
  | { readonly code: "ALREADY_VISITED"; readonly object: ObjectId }
  | { readonly code: "WRONG_TERRAIN" }
  | { readonly code: "NOT_IMPLEMENTED"; readonly what: string };

export type ExecuteError =
  | { readonly kind: "ILLEGAL"; readonly reason: Reason }
  | { readonly kind: "MALFORMED"; readonly issues: readonly string[] };

export const illegal = (reason: Reason): ExecuteError => ({ kind: "ILLEGAL", reason });
export const malformed = (...issues: string[]): ExecuteError => ({ kind: "MALFORMED", issues });

/** A human-readable rendering, for logs and tooltips. The UI may localise instead. */
export function reasonText(r: Reason): string {
  switch (r.code) {
    case "NOT_YOUR_TURN": return "It is not your turn.";
    case "GAME_OVER": return "The game is over.";
    case "PENDING_INTERACTION": return "Answer the current prompt first.";
    case "NO_SUCH_ENTITY": return `No such entity: ${r.id}`;
    case "NOT_OWNED": return `You do not control ${r.id}.`;
    case "INSUFFICIENT_MOVEMENT": return `Not enough movement: ${r.have} of ${r.need}.`;
    case "PATH_BLOCKED": return `The path is blocked at step ${r.atIndex}.`;
    case "NOT_ADJACENT": return "Too far away.";
    case "INSUFFICIENT_RESOURCES": return `Not enough ${r.resource}: ${r.have} of ${r.need}.`;
    case "ALREADY_BUILT": return "Already built.";
    case "BUILD_LIMIT_REACHED": return "This town has already built today.";
    case "MISSING_PREREQUISITE": return `Requires ${r.building} first.`;
    case "NONE_AVAILABLE": return `Only ${r.have} available, ${r.need} requested.`;
    case "ARMY_FULL": return "The army has no free slot.";
    case "SLOT_EMPTY": return "That slot is empty.";
    case "SLOT_OCCUPIED": return "That slot is taken.";
    case "LAST_STACK": return "A hero cannot be left with no army.";
    case "HERO_LIMIT_REACHED": return `You may command at most ${r.limit} heroes.`;
    case "OUT_OF_MANA": return `Not enough mana: ${r.have} of ${r.need}.`;
    case "SPELL_NOT_KNOWN": return "That spell is not in the spellbook.";
    case "NO_SPELLBOOK": return "This hero has no spellbook.";
    case "ALREADY_CAST_THIS_ROUND": return "Already cast a spell this round.";
    case "TARGET_INVALID": return "Invalid target.";
    case "OUT_OF_RANGE": return `Out of range: ${r.distance} of ${r.max}.`;
    case "NO_SHOTS_LEFT": return "No ammunition left.";
    case "STACK_NOT_ACTIVE": return "That stack is not the active one.";
    case "STACK_DEAD": return "That stack is destroyed.";
    case "HEX_OCCUPIED": return "That hex is occupied.";
    case "NO_BATTLE_IN_PROGRESS": return "No battle is in progress.";
    case "BATTLE_IN_PROGRESS": return "A battle is in progress.";
    case "NOT_IN_TACTICS_PHASE": return "Not in the tactics phase.";
    case "ALREADY_VISITED": return "This hero has already visited it.";
    case "WRONG_TERRAIN": return "Wrong terrain.";
    case "NOT_IMPLEMENTED": return `Not implemented: ${r.what}`;
  }
}
